/**
 * PrivacyChoicesDialog - Shown once for brand-new accounts, right after
 * registration is confirmed, before any other onboarding step.
 *
 * Every option starts off, matching the off-by-default account settings.
 * Saves itself via the same endpoints SettingsPage uses, and only needs
 * `onContinue` to move on. To relocate this in the flow, change where
 * it's rendered/triggered — nothing here needs to change.
 */

import React, { useState } from 'react';
import api from '../services/api';
import './PrivacyChoicesDialog.css';

const DEFAULT_PREFERENCES = {
  authorize_ai: false,
  automate_titles: false,
  enable_followups: false,
  auto_enhance: false,
  transcription_preference: 'never',
  notify_friend_suggestions: false,
  notify_when_friend_responds: false,
  sms_reminders_enabled: false,
};

function ToggleRow({ label, checked, onChange, disabled }) {
  return (
    <div className={`privacy-choices-row${disabled ? ' disabled' : ''}`}>
      <span className="privacy-choices-row-label">{label}</span>
      <label className="toggle-switch">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
        <span className={`toggle-switch-track${checked ? ' active' : ''}${disabled ? ' disabled' : ''}`}>
          <span className={`toggle-switch-knob${checked ? ' active' : ''}`}></span>
        </span>
      </label>
    </div>
  );
}

const STATUS_BADGE_CLASS = {
  'All On': 'on',
  'All Off': 'off',
  Customized: 'mixed',
};

function summarizeStatus(flags) {
  if (flags.every(Boolean)) return 'All On';
  if (flags.every((flag) => !flag)) return 'All Off';
  return 'Customized';
}

function PrivacySection({ title, description, status, onAllowAll, onDenyAll, expanded, onToggleExpand, disabled, note, children }) {
  return (
    <div className="privacy-choices-section">
      <div className="privacy-choices-section-header">
        <h3 className="privacy-choices-section-title">{title}</h3>
        <span className={`privacy-choices-status-badge ${STATUS_BADGE_CLASS[status]}`}>{status}</span>
      </div>
      <p className="privacy-choices-section-description">{description}</p>
      <div className="privacy-choices-bulk-actions">
        <button
          type="button"
          className="privacy-choices-bulk-btn allow"
          onClick={onAllowAll}
          disabled={disabled}
        >
          Allow All
        </button>
        <button
          type="button"
          className="privacy-choices-bulk-btn"
          onClick={onDenyAll}
          disabled={disabled}
        >
          Turn Off All
        </button>
      </div>
      {note && <p className="privacy-choices-note">{note}</p>}
      <button
        type="button"
        className="privacy-choices-customize-toggle"
        onClick={onToggleExpand}
      >
        {expanded ? 'Hide details ▴' : 'Customize ▾'}
      </button>
      {expanded && (
        <div className="privacy-choices-details">
          {children}
        </div>
      )}
    </div>
  );
}

function PrivacyChoicesDialog({ user, onContinue }) {
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(false);
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);
  const [aiExpanded, setAiExpanded] = useState(false);

  const hasSmsConsent = Boolean(user?.smsConsentGiven && user?.phone && user?.phoneVerified);

  const savePreference = async (key, value) => {
    let previous;
    setPreferences((prev) => {
      previous = prev[key];
      return { ...prev, [key]: value };
    });
    try {
      await api.put('/preferences', { preferenceKey: key, preferenceValue: value });
    } catch (err) {
      console.error(`Error saving preference ${key}:`, err);
      setPreferences((prev) => ({ ...prev, [key]: previous }));
    }
  };

  const saveEmailNotifications = async (value) => {
    setEmailNotificationsEnabled(value);
    try {
      await api.put('/users/me', { emailNotificationsEnabled: value });
    } catch (err) {
      console.error('Error saving email notification preference:', err);
      setEmailNotificationsEnabled(!value);
    }
  };

  const handleNotificationsBulk = async (allow) => {
    setSaving(true);
    try {
      const writes = [
        saveEmailNotifications(allow),
        savePreference('notify_friend_suggestions', allow),
        savePreference('notify_when_friend_responds', allow),
      ];
      if (hasSmsConsent) {
        writes.push(savePreference('sms_reminders_enabled', allow));
      }
      await Promise.all(writes);
    } finally {
      setSaving(false);
    }
  };

  const handleAiBulk = async (allow) => {
    setSaving(true);
    try {
      await Promise.all([
        savePreference('authorize_ai', allow),
        savePreference('automate_titles', allow),
        savePreference('enable_followups', allow),
        savePreference('auto_enhance', allow),
        savePreference('transcription_preference', allow ? 'always' : 'never'),
      ]);
    } finally {
      setSaving(false);
    }
  };

  const aiAuthorized = preferences.authorize_ai === true || preferences.authorize_ai === 'true';
  const followupsEnabled = preferences.enable_followups === true || preferences.enable_followups === 'true';

  const notificationsStatus = summarizeStatus([
    emailNotificationsEnabled,
    preferences.notify_friend_suggestions === true,
    preferences.notify_when_friend_responds === true,
    ...(hasSmsConsent ? [preferences.sms_reminders_enabled === true] : []),
  ]);

  const aiStatus = summarizeStatus([
    aiAuthorized,
    aiAuthorized && (preferences.automate_titles === true || preferences.automate_titles === 'true'),
    aiAuthorized && followupsEnabled,
    aiAuthorized && followupsEnabled && (preferences.auto_enhance === true || preferences.auto_enhance === 'true'),
    aiAuthorized && preferences.transcription_preference === 'always',
  ]);

  return (
    <div className="privacy-choices-overlay">
      <div className="privacy-choices-dialog">
        <h2 className="privacy-choices-title">Your Privacy Choices</h2>
        <p className="privacy-choices-intro">
          Everything below starts turned off. Choose what you'd like to allow — you can change any of
          this anytime in Settings.
        </p>

        <PrivacySection
          title="Notifications"
          description="Email and text reminders, plus alerts when friends suggest or respond to prompts."
          status={notificationsStatus}
          onAllowAll={() => handleNotificationsBulk(true)}
          onDenyAll={() => handleNotificationsBulk(false)}
          expanded={notificationsExpanded}
          onToggleExpand={() => setNotificationsExpanded((v) => !v)}
          disabled={saving}
        >
          <ToggleRow
            label="Email reminders"
            checked={emailNotificationsEnabled}
            onChange={saveEmailNotifications}
            disabled={saving}
          />
          {hasSmsConsent ? (
            <ToggleRow
              label="Text message reminders"
              checked={preferences.sms_reminders_enabled === true}
              onChange={(v) => savePreference('sms_reminders_enabled', v)}
              disabled={saving}
            />
          ) : user?.smsConsentGiven && user?.phone ? (
            <p className="privacy-choices-note">
              Verify your phone number in Settings to turn on text reminders.
            </p>
          ) : (
            <p className="privacy-choices-note">
              Text reminders need a verified phone number and SMS consent, which you can add in Settings.
            </p>
          )}
          <ToggleRow
            label="Friend prompt suggestions"
            checked={preferences.notify_friend_suggestions === true}
            onChange={(v) => savePreference('notify_friend_suggestions', v)}
            disabled={saving}
          />
          <ToggleRow
            label="Friend response alerts"
            checked={preferences.notify_when_friend_responds === true}
            onChange={(v) => savePreference('notify_when_friend_responds', v)}
            disabled={saving}
          />
        </PrivacySection>

        <PrivacySection
          title="AI Usage"
          description="AI is used for transcription, auto-titling, and follow-up prompts — it adds a lot to the experience, but nothing will be sent to any service without your permission."
          status={aiStatus}
          onAllowAll={() => handleAiBulk(true)}
          onDenyAll={() => handleAiBulk(false)}
          expanded={aiExpanded}
          onToggleExpand={() => setAiExpanded((v) => !v)}
          disabled={saving}
          note={(
            <>
              OpenAI doesn't use your reflections to train its models.{' '}
              <a
                href="https://openai.com/policies/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more about OpenAI's privacy policy
              </a>
            </>
          )}
        >
          <ToggleRow
            label="Authorize AI"
            checked={aiAuthorized}
            onChange={(v) => savePreference('authorize_ai', v)}
            disabled={saving}
          />
          <div className={`privacy-choices-row${!aiAuthorized ? ' disabled' : ''}`}>
            <span className="privacy-choices-row-label">Transcription</span>
            <select
              value={aiAuthorized ? preferences.transcription_preference : 'never'}
              onChange={(e) => savePreference('transcription_preference', e.target.value)}
              disabled={saving || !aiAuthorized}
            >
              <option value="never">Never Transcribe</option>
              <option value="on_demand">Only when I choose</option>
              <option value="always">Always transcribe</option>
            </select>
          </div>
          <ToggleRow
            label="Automate titles"
            checked={aiAuthorized && (preferences.automate_titles === true || preferences.automate_titles === 'true')}
            onChange={(v) => savePreference('automate_titles', v)}
            disabled={saving || !aiAuthorized}
          />
          <ToggleRow
            label="Follow-up questions"
            checked={aiAuthorized && followupsEnabled}
            onChange={(v) => savePreference('enable_followups', v)}
            disabled={saving || !aiAuthorized}
          />
          <ToggleRow
            label="Auto-generate follow-ups"
            checked={aiAuthorized && followupsEnabled && (preferences.auto_enhance === true || preferences.auto_enhance === 'true')}
            onChange={(v) => savePreference('auto_enhance', v)}
            disabled={saving || !aiAuthorized || !followupsEnabled}
          />
        </PrivacySection>

        <p className="privacy-choices-footer-note">
          You can change any of these anytime in Settings. By continuing, you agree to our{' '}
          <a href="?page=terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          {' '}and{' '}
          <a href="?page=privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>

        <button
          type="button"
          className="privacy-choices-continue-btn"
          onClick={onContinue}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

export default PrivacyChoicesDialog;
